import React, { useState, useEffect, useRef } from 'react';
import ContentWrapper from '../../../Layout/ContentWrapper';
import { Button, Col } from 'reactstrap';
import * as applicantService from '../../Services/applicantService';
import * as queryString from 'query-string';
import moment from 'moment/min/moment-with-locales';
import swal from 'sweetalert';
import produce from 'immer';
import CommentsModal from '../Modals/commentModal';
import * as utils from '../../Services/utils';
import { useTranslation } from 'react-i18next';
import CommentsTimelineCard from './commentsTimelineCard';
import HistoryItemTimelineCard from './historyItemTimelineCard';
import { basicDateFormat } from '../../Helpers/dateTimeHelper';
import { SpinnerLoader } from '../shared/spinnerLoader';

const CommentsAndHistoryTimeline = ({
  location,
  history,
  applicantId,
  applicantHistory,
  deleteFromApplicantHistory,
  updateApplicantHistory,
  loadComments = true,
}) => {
  const [state, setState] = useState({
    modalPurpose: '',
    editComment: false,
    dropdownOpen: false,
    comments: [{}],
    comment: {
      id: 0,
      title: '',
      message: '',
      rating: 0,
    },
    displayLatest: false, // display all latest comments or display for specific applicant
    showModal: false,
  });

  // IMPORTANT: if we get applicantHistory from props, then we handle the whole component as a history timeline,
  // otherwise we handle it as a comments timeline

  const { t } = useTranslation('translations');

  const dateCreatedComment = useRef('');

  useEffect(() => {
    // applicantHistory is provided, we render the whole applicant history, otherwise only the comments
    if (!applicantHistory && loadComments) {
      const parsed = queryString.parse(location?.search);

      const fetchComments = async () => {
        if (parsed['latest']) {
          const { data: comments, headers } = await applicantService.getCommentsForTenant();
          setState((prevState) => ({ ...prevState, displayLatest: true, comments }));
        } else if (parsed['applicantId']) {
          let applicantIdLocal = parsed['applicantId'];
          const { data: comments, headers } = await applicantService.getCommentsForApplicant(applicantIdLocal);
          setState((prevState) => ({ ...prevState, displayLatest: false, comments, applicantId: applicantIdLocal }));
        } else {
          const { data: comments, headers } = await applicantService.getCommentsForApplicant(applicantId);
          setState((prevState) => ({ ...prevState, displayLatest: false, comments, applicantId }));
        }
      };
      fetchComments();
    }
  }, [location?.search]);

  const isDateDifferentForComments = (date) => {
    let newDate = moment.utc(date).format(basicDateFormat);
    if (state.comments.length === 1) {
      dateCreatedComment.current = '';
    }

    if (newDate !== dateCreatedComment.current) {
      dateCreatedComment.current = newDate;
      return true;
    } else {
      return false;
    }
  };

  const isDateDifferentForEvents = (historyItem) => {
    // Find the index of the item with the given id
    let itemIndex = applicantHistory.findIndex((item) => item.id === historyItem.id);

    // If the item with the given id is found and it's not the first item in the array
    if (itemIndex > 0) {
      // Return the previous item
      const previousHistoryItem = applicantHistory[itemIndex - 1];
      const givenDate = moment(historyItem.created).date();
      const dateOfPreviousItem = moment(previousHistoryItem.created).date();

      if (givenDate !== dateOfPreviousItem) {
        return true;
      }
      return false;
    }

    // If the item with the given id is not found or it's the first item in the array, return null
    return true;
  };

  const toggleCommentsModal = (editOrViewOrCreate, item) => {
    if (editOrViewOrCreate === 'create') {
      setState((prevState) =>
        produce(prevState, (draft) => {
          draft.comment.id = 0;
          draft.comment.message = '';
          draft.comment.title = '';
          draft.comment.rating = 0;
          draft.editComment = true;
        })
      );
    } else if (editOrViewOrCreate === 'edit') {
      setState((prevState) =>
        produce(prevState, (draft) => {
          draft.editComment = true;
          draft.comment.id = item.id;
          draft.comment.message = item.message;
          draft.comment.title = item.title;
          draft.comment.rating = item.rating;
        })
      );
    } else if (editOrViewOrCreate === 'view') {
      setState((prevState) =>
        produce(prevState, (draft) => {
          draft.editComment = false;
          draft.comment.id = item.id;
          draft.comment.message = item.message;
          draft.comment.title = item.title;
          draft.comment.rating = item.rating;
        })
      );
    }
    setState((prevState) => ({ ...prevState, showModal: !prevState.showModal }));
  };

  const handleValidCommentSubmit = (values) => {
    if (values.commentId === '') {
      values.commentId = 0;
    }

    addOrUpdateCommentToApplicant({
      ...values,
      commentMessage: values.messageTextArea,
      rating: values.commentRating,
      applicantId: state.applicantId ? state.applicantId : applicantId,
    });
    toggleCommentsModal('close', null);
  };

  const editComment = (item) => {
    setState((prevState) => ({ ...prevState, applicantId: item.applicantId }));
    toggleCommentsModal('edit', item);
  };

  const viewComment = (item) => {
    toggleCommentsModal('view', item);
  };

  const addOrUpdateCommentToApplicant = (comment) => {
    if (!applicantHistory) {
      addOrUpdateOnlyComment(state, comment, setState);
    } else {
      addOrUpdateHistoryItem(comment, updateApplicantHistory);
    }
  };

  const deleteComment = (item) => {
    swal(utils.getWarningSwal()).then((willDelete) => {
      if (willDelete) {
        if (!applicantHistory) {
          const originalComments = state.comments;
          const filteredComments = state.comments.filter((c) => c.id !== item.id);

          setState(
            produce((draft) => {
              draft.comments = filteredComments;
            })
          );

          applicantService.deleteComment(item.id).then(
            (response) => {
              deleteFromApplicantHistory(item.id);
            },
            (error) => {
              setState(
                produce((draft) => {
                  draft.comments = originalComments;
                })
              );
            }
          );
        } else {
          applicantService.deleteComment(item.id).then(
            (response) => {
              deleteFromApplicantHistory(item.id);
            },
            (error) => {}
          );
        }
      }
    });
  };

  const addOrUpdateHistoryItem = (comment, updateApplicantHistory) => {
    applicantService
      .addOrUpdateCommentToApplicant(comment)
      .then((response) => {
        updateApplicantHistory(comment);
      })
      .catch((error) => {
        //setApplicantHistory(unchangedApplicantHistory);
      });
  };

  const addOrUpdateOnlyComment = (state, comment, setState) => {
    let comments = JSON.parse(JSON.stringify(state.comments));
    let index = comments.findIndex((x) => x.id === comment.commentId);
    let originalComments = JSON.parse(JSON.stringify(state.comments));

    if (comment.commentId !== 0 && comment.commentId !== '') {
      if (index !== -1) {
        comments[index].title = comment.commentTitle;
        comments[index].message = comment.commentMessage;
        comments[index].rating = comment.rating;
      }
    }

    setState(
      produce((draft) => {
        draft.comments = comments;
      })
    );

    applicantService
      .addOrUpdateCommentToApplicant(comment)
      .then((response) => {
        const { data: returnedComments } = response;
        setState(
          produce((draft) => {
            draft.comments = returnedComments;
          })
        );
      })
      .catch((error) => {
        setState(
          produce((draft) => {
            draft.comments = originalComments;
          })
        );
      });
  };

  const goToApplicant = (applicantId) => {
    history.push(`/applications/details?applicantId=${applicantId}&tab=applicationDetails`);
  };

  const firstComment = state.comments && state.comments[0];
  const Heading = (
    <div className="content-heading">
      <Col>
        {state.displayLatest ? (
          <span className="mx-2">
            <h2>{t('comments.RECENT_COMMENTS')}</h2>
          </span>
        ) : firstComment ? (
          <>
            {t('comments.COMMENTS_FOR_APPLICANT')}
            <a
              href={`/applications/details?applicantId=${firstComment.applicantId}`}
              onClick={() => goToApplicant(firstComment.applicantId)}
            >
              {firstComment.applicantFirstName} {firstComment.applicantLastName}
            </a>
          </>
        ) : (
          t('comments.NO_COMMENTS_YET_COMMENTS_FOR_APPLICANT')
        )}
      </Col>
      {!state.displayLatest && (
        <Col>
          <Button
            id="btnCreteNewNotFromTimeline"
            color="primary"
            className="float-right"
            size="lg"
            onClick={() => toggleCommentsModal('create', null)}
          >
            <b style={{ fontSize: 'large' }}> {t('comments.NEW_COMMENT')}</b>
          </Button>
        </Col>
      )}
    </div>
  );

  return (
    <>
      <CommentsModal
        showModal={state.showModal}
        comment={state.comment}
        editComment={state.editComment}
        onToggle={(value) => toggleCommentsModal(value, null)}
        onHandleValidSubmit={handleValidCommentSubmit}
      />
      {!applicantId && Heading}
      {/* START timeline */}
      {state.loading ? (
        <SpinnerLoader />
      ) : (applicantHistory && applicantHistory.length > 0) || (state.comments && state.comments.length > 0) ? (
        <ul className="timeline-alt m-4">
          {applicantHistory
            ? applicantHistory.map((historyItem, index) =>
                historyItem.type === 'comment' ? (
                  <CommentsTimelineCard
                    key={`${historyItem.id}-${index}`}
                    comment={historyItem}
                    index={index}
                    deleteComment={deleteComment}
                    displayLatest={state.displayLatest}
                    isDateDifferent={isDateDifferentForEvents}
                    editComment={editComment}
                    goToApplicant={goToApplicant}
                    viewComment={viewComment}
                    mode="full"
                  />
                ) : (
                  <HistoryItemTimelineCard
                    key={historyItem.id}
                    historyItem={historyItem}
                    index={index}
                    isDateDifferentForEvents={isDateDifferentForEvents}
                  />
                )
              )
            : state.comments.map((comment, index) => (
                <CommentsTimelineCard
                  key={`${comment.id}-${index}`}
                  comment={comment}
                  index={index}
                  deleteComment={deleteComment}
                  displayLatest={state.displayLatest}
                  isDateDifferent={isDateDifferentForComments}
                  editComment={editComment}
                  goToApplicant={goToApplicant}
                  viewComment={viewComment}
                />
              ))}
          <li className="timeline-end">
            <div className="timeline-badge">.</div>
          </li>
        </ul>
      ) : (
        t('comments.NO_COMMENTS_YET')
      )}
      {/* END timeline */}
    </>
  );
};

export default CommentsAndHistoryTimeline;
